/**
 * Session Invalidation Handler
 * 
 * Handles automatic logout and redirect when agent access is revoked
 * This ensures users are properly logged out when their agent is disabled or keys are changed
 */

import axiosInstance from '../api/axios';
import store from '../components/kit/Redux/Store'; // Redux store
import { 
  updateUserProfile, 
  updateAgent, 
  updateSelectedStore,
  clearAmendmentFlow,
  clearCurrentTimeSlotOrders
} from '../components/kit/Redux/Actions';

// Track if we're already handling a session invalidation to prevent loops
let isHandlingInvalidation = false;

/**
 * Clear all authentication data
 */
const clearAuthData = () => {
  try {
    // Clear Redux state by setting empty values
    store.dispatch(updateUserProfile({}));
    store.dispatch(updateAgent({}));
    store.dispatch(updateSelectedStore({}));
    store.dispatch(clearAmendmentFlow());
    store.dispatch(clearCurrentTimeSlotOrders());
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('agent');
    localStorage.removeItem('store');
    localStorage.removeItem('token');
    localStorage.removeItem('agentKey');
    localStorage.removeItem('storeId');
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear any cached data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sprite-') || key.startsWith('cache-'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing auth data:', error.message);
  }
};

/**
 * Show error message and redirect
 */
const handleSessionInvalidation = (reason) => {
  if (isHandlingInvalidation) {
    return; // Prevent multiple simultaneous invalidations
  }
  
  isHandlingInvalidation = true;
  
  // Clear all auth data
  clearAuthData();
  
  // Show user-friendly message
  const message = reason || 'Your session has expired. Please login again.';
  
  // Check if we have Telegram WebApp available
  if (window.Telegram && window.Telegram.WebApp) {
    const webApp = window.Telegram.WebApp;
    
    try {
      // Show alert to user
      webApp.showAlert(
        '🔐 Access Revoked\n\n' + message + '\n\nYou will be redirected to login.',
        () => {
          // Close the web app or redirect to home
          if (webApp.close) {
            webApp.close();
          } else {
            // Redirect to home/login page
            window.location.href = '/';
          }
        }
      );
    } catch (telegramError) {
      // Fallback to regular alert
      alert('🔐 Access Revoked\n\n' + message);
      window.location.href = '/';
    }
  } else {
    // Fallback for non-Telegram environments
    alert('🔐 Access Revoked\n\n' + message);
    window.location.href = '/';
  }
  
  // Reset flag after a delay
  setTimeout(() => {
    isHandlingInvalidation = false;
  }, 5000);
};

/**
 * Setup axios interceptors for handling 403 errors
 */
export const setupSessionInterceptors = () => {
  // Response interceptor on the axios instance used by postToAPI
  const interceptorId = axiosInstance.interceptors.response.use(
    (response) => {
      // Success - just return the response
      return response;
    },
    (error) => {
      // Check for 403 Access Revoked errors
      if (error.response && error.response.status === 403) {
        const data = error.response.data;
        
        // Check if this is an access revoked error (from our JWT middleware)
        if (data && data.message === 'Access revoked') {
          // Handle the session invalidation
          handleSessionInvalidation(data.reason);
          
          // Stop the error from propagating to prevent further processing
          return Promise.reject({
            ...error,
            handled: true,
            message: 'Session invalidated - user will be redirected'
          });
        }
      }
      
      // For other errors, just pass them through
      return Promise.reject(error);
    }
  );
};

/**
 * Manual session invalidation (can be called from other components)
 */
export const invalidateSession = (reason) => {
  handleSessionInvalidation(reason);
};

/**
 * Check if the current session is valid
 */
export const checkSessionValidity = async () => {
  try {
    // Make a simple authenticated request to check if session is valid
    const response = await axiosInstance.get('/api/user/auth');
    return response.data && response.data.user;
  } catch (error) {
    if (error.response && error.response.status === 403) {
      return false;
    }
    // For other errors, assume session might still be valid
    return true;
  }
};

export default {
  setupSessionInterceptors,
  invalidateSession,
  checkSessionValidity,
  clearAuthData
};